import {
  getGetFrontRichiesteIdModuloIdDocumentoConsolidatoQueryOptions,
  getGetFrontRichiesteIdModuloIdDocumentoFirmatoQueryOptions,
  getGetFrontRichiesteIdModuloIdDocumentoGeneratoQueryOptions,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  useAuthDownload,
  useHasDocumentsInlineViewer,
} from '@framework/common-web';
import { useTranslation } from '@framework/i18n';
import { Button, Tooltip, message } from 'antd';
import React, { useCallback } from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { useTheme } from 'styled-components';
import { useButtonResponsiveProps } from './useButtonResponsiveProps';

interface Props {
  submissionId?: string | null;
  submissionModuleId?: string | null;
  disabled?: boolean;
  /** Permette di forzare la visualizzazione desktop */
  ignoreMobile?: boolean;
  /** Pulsante di download in linea */
  inline?: boolean;
  /** Mostra sempre l'icona */
  forceIcon?: boolean;
  /** Specifica una funzione di download dedicato */
  downloadType?: SubmissionDownloadType;
  /** Specifica se il modulo da scaricare è firmato */
  isModuleSigned?: boolean;
}

export type SubmissionDownloadType = 'generato' | 'firmato' | 'consolidato';

/**
 * Utilizza la funzione di download per scaricare il modulo richiesto.
 */
function getDownloadTypeQueryOptionsFn(downloadType: SubmissionDownloadType) {
  switch (downloadType) {
    case 'generato':
      return getGetFrontRichiesteIdModuloIdDocumentoGeneratoQueryOptions;
    case 'firmato':
      return getGetFrontRichiesteIdModuloIdDocumentoFirmatoQueryOptions;
    case 'consolidato':
    default:
      return getGetFrontRichiesteIdModuloIdDocumentoConsolidatoQueryOptions;
  }
}

export function DownloadSubmissionModuleButton(props: Props) {
  const {
    submissionId,
    forceIcon,
    ignoreMobile,
    submissionModuleId,
    downloadType = 'consolidato',
    isModuleSigned,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  const getQueryOptions = getDownloadTypeQueryOptionsFn(downloadType);
  const hasInlineViewer = useHasDocumentsInlineViewer();

  const { download, isFetching } = useAuthDownload(
    getQueryOptions,
    [submissionId!, submissionModuleId!],
    {
      onError: e => {
        console.error(`[DownloadSubmissionModuleButton] Error: `, { error: e }); // prettier-ignore
        message.error(getNetworkErrorMessage(e, t("submission-module-button.errore-in-download",'Si è verificato un problema durante il download del file'))); // prettier-ignore
      },
      showInlineViewer: hasInlineViewer,
      openInNewWindow: theme.openModulePdfInNewWindow,
    },
  );

  const isMobile = useBreakpoint(down('md'));
  const buttonText = hasInlineViewer
    ? t('submission-module-button.visualizza-pdf', 'Visualizza PDF')
    : isModuleSigned
    ? t('submission-module-button.scarica-pdf-firmato', 'Scarica PDF')
    : t('submission-module-button.scarica-pdf', 'Scarica PDF');

  const buttonResponsiveProps = useButtonResponsiveProps({
    buttonText,
    forceIcon,
    hasInlineViewer,
    ignoreMobile,
    isMobile,
    inline: props.inline,
  });

  const handleClick = useCallback(() => {
    if (submissionId && submissionModuleId && !props.disabled) {
      download();
    }
  }, [download, props.disabled, submissionId, submissionModuleId]);

  return (
    <Tooltip
      title={
        props.disabled
          ? t(
              'form.salva-modulo-download-pdf',
              'Salva il modulo per scaricare il documento PDF',
            )
          : undefined
      }
    >
      <Button
        {...buttonResponsiveProps}
        onClick={handleClick}
        disabled={!submissionId || !submissionModuleId || props.disabled}
        loading={isFetching}
        block={(isMobile && !ignoreMobile) ?? props.inline ? undefined : true}
        size="large"
        type={props.inline ? 'link' : 'default'}
        // icon={props.inline ? <DownloadOutlined aria-hidden /> : undefined}
      />
    </Tooltip>
  );
}
